import React, { useState, useEffect, useRef } from "react";
import api from "../../../api";

/**
 * ForwardingApprovalModal
 *
 * Props:
 *   open          {bool}     - whether modal is visible
 *   onClose       {fn}       - called when modal closes
 *   emails        {string[]} - list of email addresses from advice passwords (for dropdown)
 *   prefillEmail  {string}   - pre-selected email (from per-row button)
 */
export default function ForwardingApprovalModal({
  open,
  onClose,
  emails = [],
  prefillEmail = "",
}) {
  const [selectedEmail, setSelectedEmail] = useState("");
  const [addingNew, setAddingNew] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [site, setSite] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      if (prefillEmail) {
        setSelectedEmail(prefillEmail);
        setAddingNew(false);
        setNewEmail("");
        setSearchTerm(prefillEmail);
      } else {
        setSelectedEmail("");
        setSearchTerm("");
      }
    }
  }, [open, prefillEmail]);

  useEffect(() => {
    if (!dropdownOpen) return;
    const handle = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [dropdownOpen]);

  const handleClose = () => {
    if (submitting) return;
    setSelectedEmail("");
    setAddingNew(false);
    setNewEmail("");
    setSearchTerm("");
    setDropdownOpen(false);
    setSite("");
    setNotes("");
    setError("");
    setSuccess(false);
    onClose();
  };

  const activeEmail = addingNew ? newEmail.trim() : selectedEmail;

  // Deduplicate emails (case-insensitive) before filtering
  const uniqueEmails = Array.from(
    new Map(emails.map((e) => [e.toLowerCase(), e])).values()
  );

  const filteredEmails = uniqueEmails.filter((e) =>
    e.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectEmail = (email) => {
    setSelectedEmail(email);
    setSearchTerm(email);
    setAddingNew(false);
    setDropdownOpen(false);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setSelectedEmail("");
    setDropdownOpen(true);
    setAddingNew(false);
  };

  const handleAddNew = () => {
    setAddingNew(true);
    setSelectedEmail("");
    setSearchTerm("");
    setDropdownOpen(false);
    setNewEmail("");
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!activeEmail) {
      setError("Please select or enter an email address.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const response = await api.post("/api/reports/forwarding-approval", {
        email: activeEmail,
        site: site.trim() || null,
        notes: notes.trim() || null,
      });
      if (response.data?.ok) {
        setSuccess(true);
        setTimeout(() => handleClose(), 2200);
      }
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Failed to send request. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="modal d-block"
      style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1055 }}
      onClick={handleClose}
    >
      <div
        className="modal-dialog modal-dialog-centered"
        style={{ maxWidth: 520 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">📧 Request Forwarding Email Approval</h5>
            <button
              type="button"
              className="btn-close"
              onClick={handleClose}
              disabled={submitting}
            />
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div
                className="alert mb-3 d-flex gap-2 align-items-start"
                style={{
                  backgroundColor: "#e8f4fd",
                  border: "1px solid #bee3f8",
                  borderRadius: 6,
                  fontSize: "0.875rem",
                  color: "#1a5276",
                }}
              >
                <span style={{ fontSize: "1.1rem", lineHeight: 1.4 }}>ℹ️</span>
                <span>
                  By requesting Forwarding Approval, a member of staff will review and
                  accept it <strong>between 8am–5pm, Monday to Friday</strong>. You will
                  then receive a <strong>Slack notification</strong> once it has been
                  approved.
                </span>
              </div>

              {success && (
                <div className="alert alert-success">
                  ✅ Request sent to staff successfully!
                </div>
              )}

              {error && <div className="alert alert-danger">❌ {error}</div>}

              {!success && (
                <>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Email Address <span className="text-danger">*</span>
                    </label>

                    {!addingNew ? (
                      <div ref={dropdownRef} className="position-relative">
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search or select an email…"
                            value={searchTerm}
                            onChange={handleSearchChange}
                            onFocus={() => setDropdownOpen(true)}
                            disabled={submitting}
                            autoComplete="off"
                          />
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => setDropdownOpen((o) => !o)}
                            disabled={submitting}
                            tabIndex={-1}
                          >
                            ▾
                          </button>
                        </div>

                        {dropdownOpen && (
                          <div
                            className="border rounded bg-white shadow-sm"
                            style={{
                              position: "absolute",
                              top: "100%",
                              left: 0,
                              right: 0,
                              zIndex: 1060,
                              maxHeight: 220,
                              overflowY: "auto",
                            }}
                          >
                            {filteredEmails.length === 0 && (
                              <div className="p-2 text-muted small">
                                No matching emails found.
                              </div>
                            )}
                            {filteredEmails.map((email) => (
                              <div
                                key={email}
                                className={`p-2 small ${
                                  selectedEmail === email
                                    ? "bg-primary text-white"
                                    : ""
                                }`}
                                style={{ cursor: "pointer" }}
                                onMouseEnter={(e) => {
                                  if (selectedEmail !== email)
                                    e.currentTarget.style.backgroundColor =
                                      "#f0f4ff";
                                }}
                                onMouseLeave={(e) => {
                                  if (selectedEmail !== email)
                                    e.currentTarget.style.backgroundColor =
                                      "transparent";
                                }}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  handleSelectEmail(email);
                                }}
                              >
                                {email}
                              </div>
                            ))}

                            <div
                              className="p-2 small border-top d-flex align-items-center gap-2"
                              style={{
                                cursor: "pointer",
                                backgroundColor: "#f0fdf4",
                                color: "#166534",
                                fontWeight: 600,
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.backgroundColor =
                                  "#dcfce7")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.backgroundColor =
                                  "#f0fdf4")
                              }
                              onMouseDown={(e) => {
                                e.preventDefault();
                                handleAddNew();
                              }}
                            >
                              <span>＋</span>
                              <span>Add a new email address</span>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="input-group">
                        <input
                          ref={inputRef}
                          type="email"
                          className="form-control"
                          placeholder="Enter new email address…"
                          value={newEmail}
                          onChange={(e) => setNewEmail(e.target.value)}
                          disabled={submitting}
                          required
                        />
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => {
                            setAddingNew(false);
                            setNewEmail("");
                          }}
                          disabled={submitting}
                          title="Back to selection"
                        >
                          ✕
                        </button>
                      </div>
                    )}

                    {activeEmail && (
                      <div className="mt-1 text-muted small">
                        Selected:{" "}
                        <strong className="text-dark">{activeEmail}</strong>
                      </div>
                    )}
                  </div>

                  {/* Site only shown when adding a brand-new email */}
                  {addingNew && (
                  <div className="mb-3">
                    <label className="form-label">
                      Site{" "}
                      <span className="text-muted fw-normal">(optional)</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. Ticketmaster"
                      value={site}
                      onChange={(e) => setSite(e.target.value)}
                      disabled={submitting}
                    />
                  </div>
                  )}

                  <div className="mb-1">
                    <label className="form-label">
                      Notes{" "}
                      <span className="text-muted fw-normal">(optional)</span>
                    </label>
                    <textarea
                      className="form-control"
                      rows="3"
                      placeholder="Any additional context for staff…"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      disabled={submitting}
                    />
                  </div>
                </>
              )}
            </div>

            {!success && (
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleClose}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submitting || !activeEmail}
                >
                  {submitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Sending…
                    </>
                  ) : (
                    "Send Request"
                  )}
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
